import React, {
    useState,
    useRef,
    useEffect,
    useCallback,
    useContext,
} from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    Paper,
    Stack,
    useTheme,
    useMediaQuery,
    Chip,
    FormControl,
    InputLabel,
    Select,
    Divider,
    Tooltip,
    Fab,
    Zoom,
} from '@mui/material'
import {
    Close,
    Delete,
    Add as AddIcon,
    Star,
    StarBorder,
    Highlight,
} from '@mui/icons-material'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import { AuthContext } from '../context/AuthContext'
import { AnnotationContext } from '../context/AnnotationContext'
import apiService from './ApiService'

/**
 * Custom hook for annotation functionality in popups
 * Provides all annotation state, handlers, and UI components
 * 
 * @param {Object} options
 * @param {string} options.documentId - Document identifier (slug)
 * @param {string} options.documentType - Type of document (article, guideline, decision, etc.)
 * @param {React.RefObject} options.containerRef - Ref to the container element
 * @param {Function} options.showSnackbar - Snackbar function for notifications
 * @param {boolean} options.enableAnnotations - Whether annotations are enabled
 * @returns {Object} Annotation state, handlers, and UI components
 */
export function usePopupAnnotations({
    documentId,
    documentType = 'popup-document',
    containerRef,
    showSnackbar,
    enableAnnotations = true,
}) {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const { isAuthenticated } = useContext(AuthContext)
    
    // Use annotation context for cross-component sync
    // Returns null if AnnotationProvider is not in the component tree
    const annotationContextValue = useContext(AnnotationContext)
    const annotationContext = annotationContextValue || null
    
    const hasCheckedInitialHash = useRef(false)

    // Theme colors
    const themeColors = {
        primary: '#232536',
        secondary: '#ffcf51',
        hover: '#3a3e5a',
    }

    // Get annotations from shared context
    const [annotations, setAnnotations] = useState([])
    const [isEditorOpen, setIsEditorOpen] = useState(false)
    const [editingAnnotation, setEditingAnnotation] = useState(null)
    const [annotationText, setAnnotationText] = useState('')
    const [currentSelection, setCurrentSelection] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [successMessage, setSuccessMessage] = useState('')

    // Enhanced annotation state
    const [selectedColor, setSelectedColor] = useState('#fff59d')
    const [selectedCategory, setSelectedCategory] = useState('general')
    const [selectedPriority, setSelectedPriority] = useState('normal')
    const [annotationTags, setAnnotationTags] = useState([])
    const [tagsInputValue, setTagsInputValue] = useState('')
    const [isImportant, setIsImportant] = useState(false)

    // Context menu state
    const [contextMenu, setContextMenu] = useState(null)

    // Tooltip state
    const [tooltip, setTooltip] = useState({
        show: false,
        content: '',
        x: 0,
        y: 0,
    })

    // Load annotations from shared context (or direct API if context unavailable)
    const loadAnnotations = useCallback(async () => {
        if (!documentId || !isAuthenticated) return

        try {
            setLoading(true)
            setError(null)
            let data
            if (annotationContext) {
                data = await annotationContext.fetchAnnotations(documentId)
            } else {
                // Fallback to direct API call if context not available
                const response = await apiService.getAnnotationsForDocument(documentId)
                data = response || []
            }
            setAnnotations(data || [])
        } catch (error) {
            console.error('Failed to load annotations:', error)
            setError('Failed to load annotations. Please try again.')
            setAnnotations([])
        } finally {
            setLoading(false)
        }
    }, [documentId, isAuthenticated, annotationContext])

    // Ref for applyHighlights to avoid subscription recreation
    const applyHighlightsRef = useRef(null)

    // Subscribe to annotation changes from context (for cross-component sync)
    useEffect(() => {
        if (!documentId || !annotationContext) {
           // console.log('[usePopupAnnotations] No subscription - documentId:', documentId, 'context:', !!annotationContext)
            return
        }
        
       // console.log('[usePopupAnnotations] Setting up subscription for:', documentId)
        
        const unsubscribe = annotationContext.subscribe(documentId, (newAnnotations) => {
           // console.log('[usePopupAnnotations] Received annotation update:', newAnnotations.length, 'annotations')
            setAnnotations(newAnnotations)
            // The useEffect watching annotations will handle re-highlighting via debouncedApplyHighlights
        })
        
        return unsubscribe
    }, [documentId, annotationContext])

    useEffect(() => {
        if (documentId && isAuthenticated && enableAnnotations) {
            loadAnnotations()
        } else if (!isAuthenticated) {
            setAnnotations([])
        }
    }, [documentId, isAuthenticated, enableAnnotations, loadAnnotations])

    // Save annotation to backend API and update shared context
    const saveAnnotation = async (annotationData) => {
        if (!isAuthenticated) {
            setError('Please log in to save annotations.')
            return null
        }

        try {
            setLoading(true)
            setError(null)
            const response = await apiService.createAnnotation(annotationData)
            const newAnnotation = response
            
            // Update local state immediately
            setAnnotations((prev) => [...prev, newAnnotation])
            
            // Update shared context for cross-component sync
            if (annotationContext) {
                annotationContext.addAnnotation(documentId, newAnnotation)
            }
            
            setSuccessMessage('Annotation saved successfully!')
            if (showSnackbar) {
                showSnackbar('Annotation saved successfully!', 'success')
            }
            return newAnnotation
        } catch (error) {
            console.error('Failed to save annotation:', error)
            setError('Failed to save annotation. Please try again.')
            if (showSnackbar) {
                showSnackbar('Failed to save annotation', 'error')
            }
            return null
        } finally {
            setLoading(false)
        }
    }

    // Update annotation in backend API and update shared context
    const updateAnnotation = async (annotationId, updateData) => {
        if (!isAuthenticated) {
            setError('Please log in to update annotations.')
            return null
        }

        try {
            setLoading(true)
            setError(null)
            const response = await apiService.updateAnnotation(annotationId, updateData)
            const updatedAnnotation = response
            
            // Update local state immediately
            setAnnotations((prev) =>
                prev.map((ann) => (ann.id === annotationId ? updatedAnnotation : ann))
            )
            
            // Update shared context for cross-component sync
            if (annotationContext) {
                annotationContext.updateAnnotation(documentId, annotationId, updatedAnnotation)
            }
            
            setSuccessMessage('Annotation updated successfully!')
            if (showSnackbar) {
                showSnackbar('Annotation updated successfully!', 'success')
            }
            return updatedAnnotation
        } catch (error) {
            console.error('Failed to update annotation:', error)
            setError('Failed to update annotation. Please try again.')
            if (showSnackbar) {
                showSnackbar('Failed to update annotation', 'error')
            }
            return null
        } finally {
            setLoading(false)
        }
    }

    // Delete annotation from backend API and update shared context
    const deleteAnnotation = async (annotationId) => {
        if (!isAuthenticated) {
            throw new Error('Please log in to delete annotations.')
        }

        try {
            setLoading(true)
            setError(null)
            await apiService.deleteAnnotation(annotationId)
            
            // Update shared context for cross-component sync
            if (annotationContext) {
                annotationContext.removeAnnotation(documentId, annotationId)
            }
            
            setSuccessMessage('Annotation deleted successfully!')
            if (showSnackbar) {
                showSnackbar('Annotation deleted successfully!', 'success')
            }
            return true
        } catch (error) {
            console.error('Failed to delete annotation:', error)
            setError('Failed to delete annotation. Please try again.')
            if (showSnackbar) {
                showSnackbar('Failed to delete annotation', 'error')
            }
            throw error
        } finally {
            setLoading(false)
        }
    }

    // Generate unique ID for annotations
    const generateId = () => {
        return `ann_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }

    // Generate XPath for a given node
    const getXPath = useCallback((node) => {
        const getNodeXPath = (currentNode) => {
            if (!currentNode) return ''

            if (currentNode.nodeType === Node.ATTRIBUTE_NODE) {
                return getNodeXPath(currentNode.ownerElement) + '/@' + currentNode.name
            }
            if (currentNode.nodeType === Node.TEXT_NODE) {
                return getNodeXPath(currentNode.parentNode)
            }
            if (currentNode.nodeType !== Node.ELEMENT_NODE) {
                return ''
            }

            if (
                !currentNode.parentNode ||
                currentNode.parentNode.nodeType === Node.DOCUMENT_NODE
            ) {
                return '/' + currentNode.tagName.toLowerCase()
            }

            let ix = 0
            const siblings = currentNode.parentNode.childNodes
            for (let i = 0; i < siblings.length; i++) {
                const sibling = siblings[i]
                if (sibling === currentNode) {
                    const tagName = currentNode.tagName.toLowerCase()
                    const parentXPath = getNodeXPath(currentNode.parentNode)
                    return parentXPath + '/' + tagName + '[' + (ix + 1) + ']'
                }
                if (
                    sibling.nodeType === Node.ELEMENT_NODE &&
                    sibling.tagName === currentNode.tagName
                ) {
                    ix++
                }
            }
            return ''
        }

        return getNodeXPath(node)
    }, [])

    // Generate context text (text before and after selection)
    const getContextText = useCallback((range, length = 50) => {
        const startContainer = range.startContainer
        const endContainer = range.endContainer

        let textBefore = ''
        if (startContainer.nodeType === Node.TEXT_NODE) {
            const beforeText = startContainer.textContent.substring(0, range.startOffset)
            textBefore = beforeText.substring(Math.max(0, beforeText.length - length))
        }

        let textAfter = ''
        if (endContainer.nodeType === Node.TEXT_NODE) {
            const afterText = endContainer.textContent.substring(range.endOffset)
            textAfter = afterText.substring(0, length)
        }

        return { textBefore, textAfter }
    }, [])

    // Detect if device is touch-enabled
    const isTouchDevice = useCallback(() => {
        return (
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0 ||
            navigator.msMaxTouchPoints > 0
        )
    }, [])

    // Get all text nodes within a range (handles cross-element selections)
    const getTextNodesInRange = useCallback((range) => {
        const textNodes = []
        const walker = document.createTreeWalker(
            range.commonAncestorContainer,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: (node) => {
                    const nodeRange = document.createRange()
                    nodeRange.selectNodeContents(node)

                    if (
                        range.compareBoundaryPoints(Range.END_TO_START, nodeRange) < 0 &&
                        range.compareBoundaryPoints(Range.START_TO_END, nodeRange) > 0
                    ) {
                        return NodeFilter.FILTER_ACCEPT
                    }
                    return NodeFilter.FILTER_REJECT
                },
            }
        )

        let node
        while ((node = walker.nextNode())) {
            textNodes.push(node)
        }

        return textNodes
    }, [])

    // Find text node at specific offset within an element
    const findTextNodeAtOffset = useCallback((element, targetOffset) => {
        const walker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT,
            null,
            false
        )

        let currentOffset = 0
        let node

        while ((node = walker.nextNode())) {
            const nodeLength = node.textContent.length
            if (currentOffset + nodeLength >= targetOffset) {
                return {
                    node: node,
                    offset: targetOffset - currentOffset,
                }
            }
            currentOffset += nodeLength
        }

        return null
    }, [])

    // Text-based range finder - fallback when XPath doesn't match
    // This is essential for popups where the DOM structure differs from the main page
    const findRangeByTextContent = useCallback((annotation, container) => {
        try {
            const { selectedText, textBefore, textAfter } = annotation

            // console.log('[DEBUG] findRangeByTextContent called for annotation:', {
            //     id: annotation.id,
            //     selectedText: selectedText?.substring(0, 50),
            //     textBeforeLength: textBefore?.length || 0,
            //     hasContainer: !!container
            // })

            if (!selectedText || !container) {
                console.warn('[DEBUG] Missing selectedText or container')
                return null
            }

            // Get all text content from the container
            const walker = document.createTreeWalker(
                container,
                NodeFilter.SHOW_TEXT,
                null,
                false
            )

            const textNodes = []
            let node
            while ((node = walker.nextNode())) {
                if (node.textContent.trim()) {
                    textNodes.push(node)
                }
            }

            if (textNodes.length === 0) return null

            // Build a map of cumulative text positions
            // Join WITHOUT separator - this preserves inline punctuation like </a>.
            const fullText = textNodes.map(n => n.textContent).join('')

            // Normalize function - removes ALL whitespace for matching
            // This handles cross-element selections where browser adds \n between block elements
            const normalizeText = (text) => text.toLowerCase().replace(/[\s\n\r\t]+/g, '')

            const normalizedSearch = normalizeText(selectedText)
            const normalizedFull = normalizeText(fullText)

            // Try to find the selected text with context
            let foundIndex = -1
            let useNormalizedMatching = false

            // First try direct match (for simple cases without newlines)
            foundIndex = fullText.indexOf(selectedText)

            // If not found, try case-insensitive direct match
            if (foundIndex === -1) {
                foundIndex = fullText.toLowerCase().indexOf(selectedText.toLowerCase())
            }

            // If not found, try with before context
            if (foundIndex === -1 && textBefore) {
                const contextSearch = textBefore + selectedText
                const contextIndex = fullText.indexOf(contextSearch)
                if (contextIndex !== -1) {
                    foundIndex = contextIndex + textBefore.length
                }
            }

            // If still not found, try normalized matching (for cross-element selections)
            // This handles cases where selectedText has \n but fullText doesn't
            if (foundIndex === -1) {
                const normalizedIndex = normalizedFull.indexOf(normalizedSearch)
                if (normalizedIndex !== -1) {
                    useNormalizedMatching = true
                    
                    // Map normalized position back to original position
                    // Since normalize removes ALL whitespace, we count non-whitespace chars only
                    let normPos = 0
                    for (let i = 0; i < fullText.length; i++) {
                        const char = fullText[i]
                        if (!/\s/.test(char)) {
                            if (normPos === normalizedIndex) {
                                foundIndex = i
                                break
                            }
                            normPos++
                        }
                    }
                }
            }

            if (foundIndex === -1) {
                console.warn('[DEBUG] Text not found. Search text:', selectedText.substring(0, 100))
                console.warn('[DEBUG] Normalized search:', normalizedSearch.substring(0, 100))
                console.warn('[DEBUG] Container text (first 200 chars):', fullText.substring(0, 200))
                return null
            }

            // console.log('[DEBUG] Text found at index:', foundIndex, 'useNormalized:', useNormalizedMatching)

            // Find the text nodes that contain the start and end of the selection
            let currentPos = 0
            let startNode = null, startOffset = 0
            let endNode = null, endOffset = 0

            // Calculate end index
            let endIndex
            if (useNormalizedMatching) {
                // For normalized matches, count non-whitespace characters to find end
                let normCount = 0
                endIndex = foundIndex
                
                for (let i = foundIndex; i < fullText.length && normCount < normalizedSearch.length; i++) {
                    const char = fullText[i]
                    if (!/\s/.test(char)) {
                        normCount++
                    }
                    endIndex = i + 1
                }
                
                // Trim trailing whitespace from endIndex
                while (endIndex > foundIndex && /\s/.test(fullText[endIndex - 1])) {
                    endIndex--
                }
            } else {
                endIndex = foundIndex + selectedText.length
            }

            for (let i = 0; i < textNodes.length; i++) {
                const textNode = textNodes[i]
                const nodeLength = textNode.textContent.length
                const nodeEnd = currentPos + nodeLength

                // Find start node
                if (!startNode && foundIndex >= currentPos && foundIndex < nodeEnd) {
                    startNode = textNode
                    startOffset = foundIndex - currentPos
                }

                // Find end node
                if (!endNode && endIndex > currentPos && endIndex <= nodeEnd) {
                    endNode = textNode
                    endOffset = endIndex - currentPos
                }

                // If we found both, we're done
                if (startNode && endNode) break

                currentPos = nodeEnd
            }

            // Handle case where end is at the very end of a text node
            if (startNode && !endNode) {
                currentPos = 0
                for (let i = 0; i < textNodes.length; i++) {
                    const textNode = textNodes[i]
                    const nodeLength = textNode.textContent.length
                    const nodeEnd = currentPos + nodeLength
                    
                    if (endIndex >= currentPos && endIndex <= nodeEnd) {
                        endNode = textNode
                        endOffset = endIndex - currentPos
                        break
                    }
                    currentPos = nodeEnd
                }
            }

            if (!startNode || !endNode) {
                console.warn('[DEBUG] Could not find start/end nodes. startNode:', !!startNode, 'endNode:', !!endNode)
                return null
            }

            // Create and return the range
            const range = document.createRange()
            range.setStart(startNode, Math.min(startOffset, startNode.textContent.length))
            range.setEnd(endNode, Math.min(endOffset, endNode.textContent.length))

            return range
        } catch (error) {
            console.error('[Annotations] Error in findRangeByTextContent:', error)
            return null
        }
    }, [])

    // Recreate range from stored XPath information
    // Falls back to text-based matching if XPath fails (common for popups)
    const recreateRangeFromXPath = useCallback(
        (annotation, container) => {
            try {
                // First, try XPath-based matching
                const getNodeByXPath = (xpath) => {
                    try {
                        const result = document.evaluate(
                            xpath,
                            document, // Use document instead of container for absolute XPaths
                            null,
                            XPathResult.FIRST_ORDERED_NODE_TYPE,
                            null
                        )
                        const node = result.singleNodeValue
                        // Verify the node is within our container
                        if (node && container.contains(node)) {
                            return node
                        }
                        return null
                    } catch (e) {
                        return null
                    }
                }

                const startNode = getNodeByXPath(annotation.startXPath)
                const endNode = getNodeByXPath(annotation.endXPath)

                if (startNode && endNode) {
                    const startTextNode = findTextNodeAtOffset(startNode, annotation.startOffset)
                    const endTextNode = findTextNodeAtOffset(endNode, annotation.endOffset)

                    if (startTextNode && endTextNode) {
                        const range = document.createRange()
                        range.setStart(startTextNode.node, startTextNode.offset)
                        range.setEnd(endTextNode.node, endTextNode.offset)

                        // Validate the range matches the expected selected text
                        const rangeText = range.toString()
                        const expectedText = annotation.selectedText

                        // Normalize for comparison (handle whitespace differences)
                        const normalize = (text) => text?.replace(/\s+/g, ' ').trim() || ''

                        if (normalize(rangeText) === normalize(expectedText)) {
                            return range  // XPath result matches expected text
                        }
                        // XPath offsets don't produce correct selection, fall through to text-based matching
                    }
                }

                // XPath failed - fall back to text-based matching
                // This is common when annotations are created on main page but displayed in popup
                return findRangeByTextContent(annotation, container)
            } catch (error) {
                console.error('[Annotations] Error recreating range:', error)
                // Try text-based fallback
                return findRangeByTextContent(annotation, container)
            }
        },
        [findTextNodeAtOffset, findRangeByTextContent]
    )

    // Edit existing annotation
    const handleEditAnnotation = useCallback((annotation) => {
        setEditingAnnotation({
            ...annotation,
            isNew: false,
            text: annotation.selectedText || annotation.text || 'Selected text unavailable',
            selectedText: annotation.selectedText || annotation.text || 'Selected text unavailable',
        })

        setAnnotationText(annotation.annotationContent || '')
        setSelectedColor(annotation.highlightColor || '#fff59d')
        setSelectedCategory(annotation.category || 'general')
        setSelectedPriority(annotation.priority || 'normal')
        setAnnotationTags(annotation.tags || [])
        setTagsInputValue((annotation.tags || []).join(', '))
        setIsImportant(annotation.isImportant || false)
        setIsEditorOpen(true)
    }, [])

    // Create highlight span with all event handlers
    const createHighlightSpan = useCallback(
        (annotation) => {
            const highlight = document.createElement('span')
            highlight.className = 'annotation-highlight'
            highlight.style.backgroundColor = annotation.highlightColor || '#fff3cd'
            highlight.style.cursor = 'pointer'
            highlight.style.borderRadius = '4px'
            highlight.style.transition = 'background-color 0.2s ease'

            highlight.dataset.annotationId = annotation.id
            highlight.setAttribute('data-annotation-id', annotation.id)

            const isTouch = isTouchDevice()
            let longPressTimer = null
            let isLongPress = false

            const handleMouseEnter = (e) => {
                if (!isTouch && !isEditorOpen) {
                    const rect = e.target.getBoundingClientRect()
                    setTooltip({
                        show: true,
                        content: annotation.annotationContent || 'Quick Highlight',
                        x: rect.left + rect.width / 2,
                        y: rect.top - 10,
                    })
                }
            }

            const handleMouseLeave = () => {
                if (!isTouch) {
                    setTooltip({ show: false, content: '', x: 0, y: 0 })
                }
            }

            const handleClick = (e) => {
                const clickedElement = e.target
                const element = clickedElement.nodeType === Node.TEXT_NODE
                    ? clickedElement.parentElement
                    : clickedElement
                const clickedLink = element?.closest('a')

                if (clickedLink) {
                    return
                }

                e.stopPropagation()
                e.preventDefault()
                if (!isLongPress) {
                    setTooltip({ show: false, content: '', x: 0, y: 0 })
                    handleEditAnnotation(annotation)
                }
                isLongPress = false
            }

            const handleTouchStart = (e) => {
                const touchedElement = e.target
                const element = touchedElement.nodeType === Node.TEXT_NODE
                    ? touchedElement.parentElement
                    : touchedElement
                const touchedLink = element?.closest('a')

                if (touchedLink) {
                    return
                }
                e.stopPropagation()
                isLongPress = false
                longPressTimer = setTimeout(() => {
                    if (!isEditorOpen) {
                        isLongPress = true
                        const rect = e.target.getBoundingClientRect()
                        setTooltip({
                            show: true,
                            content: annotation.annotationContent || 'Quick Highlight',
                            x: rect.left + rect.width / 2,
                            y: rect.top - 10,
                        })
                    }
                }, 500)
            }

            const handleTouchMove = () => {
                if (longPressTimer) {
                    clearTimeout(longPressTimer)
                    longPressTimer = null
                    isLongPress = false
                }
            }

            const handleTouchEnd = (e) => {
                const touchedElement = e.target
                const element = touchedElement.nodeType === Node.TEXT_NODE
                    ? touchedElement.parentElement
                    : touchedElement
                const touchedLink = element?.closest('a')

                if (touchedLink) {
                    if (longPressTimer) {
                        clearTimeout(longPressTimer)
                        longPressTimer = null
                    }
                    isLongPress = false
                    return
                }
                e.stopPropagation()
                if (longPressTimer) {
                    clearTimeout(longPressTimer)
                    longPressTimer = null
                }
                if (isLongPress) {
                    setTimeout(() => {
                        setTooltip({ show: false, content: '', x: 0, y: 0 })
                        isLongPress = false
                    }, 2000)
                }
            }

            const handleSelectStart = (e) => {
                const selectedElement = e.target
                const element = selectedElement.nodeType === Node.TEXT_NODE
                    ? selectedElement.parentElement
                    : selectedElement
                const selectedLink = element?.closest('a')

                if (selectedLink) {
                    return true
                }

                e.preventDefault()
                return false
            }

            if (!isTouch) {
                highlight.addEventListener('mouseenter', handleMouseEnter)
                highlight.addEventListener('mouseleave', handleMouseLeave)
            }
            highlight.addEventListener('click', handleClick)
            highlight.addEventListener('selectstart', handleSelectStart)
            if (isTouch) {
                highlight.addEventListener('touchstart', handleTouchStart, { passive: true })
                highlight.addEventListener('touchmove', handleTouchMove, { passive: true })
                highlight.addEventListener('touchend', handleTouchEnd, { passive: true })
            }

            highlight._cleanup = () => {
                if (longPressTimer) clearTimeout(longPressTimer)
                if (!isTouch) {
                    highlight.removeEventListener('mouseenter', handleMouseEnter)
                    highlight.removeEventListener('mouseleave', handleMouseLeave)
                }
                highlight.removeEventListener('click', handleClick)
                highlight.removeEventListener('selectstart', handleSelectStart)
                if (isTouch) {
                    highlight.removeEventListener('touchstart', handleTouchStart)
                    highlight.removeEventListener('touchmove', handleTouchMove)
                    highlight.removeEventListener('touchend', handleTouchEnd)
                }
            }

            return highlight
        },
        [handleEditAnnotation, isEditorOpen, isTouchDevice, setTooltip]
    )

    // Highlight single text node (simple case)
    const highlightSingleNode = useCallback(
        (range, annotation) => {
            const highlight = createHighlightSpan(annotation)
            highlight.textContent = range.toString()

            range.deleteContents()
            range.insertNode(highlight)
        },
        [createHighlightSpan]
    )

    // Highlight multiple text nodes (cross-element case)
    const highlightMultipleNodes = useCallback(
        (range, textNodes, annotation) => {
            const startContainer = range.startContainer
            const endContainer = range.endContainer
            const startOffset = range.startOffset
            const endOffset = range.endOffset

            textNodes.forEach((textNode, index) => {
                try {
                    const isFirst = textNode === startContainer
                    const isLast = textNode === endContainer

                    let nodeStartOffset = 0
                    let nodeEndOffset = textNode.textContent.length

                    if (isFirst) {
                        nodeStartOffset = startOffset
                    }
                    if (isLast) {
                        nodeEndOffset = endOffset
                    }

                    if (nodeStartOffset >= nodeEndOffset) {
                        return
                    }

                    const nodeRange = document.createRange()
                    nodeRange.setStart(textNode, nodeStartOffset)
                    nodeRange.setEnd(textNode, nodeEndOffset)

                    const highlight = createHighlightSpan(annotation)
                    highlight.dataset.segmentIndex = index
                    highlight.dataset.totalSegments = textNodes.length

                    const highlightedText = nodeRange.toString()
                    highlight.textContent = highlightedText

                    nodeRange.deleteContents()
                    nodeRange.insertNode(highlight)
                } catch (error) {
                    console.error(`Error highlighting node segment ${index}:`, error)
                }
            })
        },
        [createHighlightSpan]
    )

    // Main highlight function - handles both single and multi-node selections
    const highlightText = useCallback(
        (annotation) => {
            if (!containerRef.current) {
                return
            }

            try {
                const range = recreateRangeFromXPath(annotation, containerRef.current)

                if (!range) {
                    return
                }

                const startContainer = range.startContainer
                const endContainer = range.endContainer

                if (
                    startContainer === endContainer &&
                    startContainer.nodeType === Node.TEXT_NODE
                ) {
                    highlightSingleNode(range, annotation)
                    return
                }

                const textNodes = getTextNodesInRange(range)

                if (textNodes.length === 0) {
                    return
                }

                if (textNodes.length === 1) {
                    highlightSingleNode(range, annotation)
                } else {
                    highlightMultipleNodes(range, textNodes, annotation)
                }
            } catch (error) {
                console.error('[Annotations] Error in highlightText:', error)
            }
        },
        [
            containerRef,
            recreateRangeFromXPath,
            getTextNodesInRange,
            highlightSingleNode,
            highlightMultipleNodes,
        ]
    )

    // Remove all highlight segments for an annotation
    const removeHighlightFromDOM = useCallback((annotationId) => {
        if (!containerRef.current) {
            return false
        }

        const highlightElements = containerRef.current.querySelectorAll(
            `[data-annotation-id="${annotationId}"]`
        )

        if (highlightElements.length === 0) {
            return false
        }

        highlightElements.forEach((highlightElement) => {
            try {
                if (highlightElement._cleanup) {
                    highlightElement._cleanup()
                }

                const parent = highlightElement.parentNode
                const textContent = highlightElement.textContent
                const textNode = document.createTextNode(textContent)

                parent.replaceChild(textNode, highlightElement)
                parent.normalize()
            } catch (error) {
                console.error('Error removing highlight:', error)
            }
        })

        return true
    }, [containerRef])

    // Close context menu
    const closeContextMenu = () => {
        setContextMenu(null)
    }

    // Quick highlight without annotation dialog
    const handleQuickHighlight = async () => {
        if (!currentSelection) return
        if (!isAuthenticated) {
            setError('Please log in to create highlights.')
            if (showSnackbar) {
                showSnackbar('Please log in to create highlights.', 'warning')
            }
            closeContextMenu()
            return
        }

        const apiData = {
            documentId,
            documentType,
            selectedText: currentSelection.selectedText,
            annotationContent: 'Quick Highlight',
            startXPath: currentSelection.startXPath,
            endXPath: currentSelection.endXPath,
            startOffset: currentSelection.startOffset,
            endOffset: currentSelection.endOffset,
            textBefore: currentSelection.textBefore,
            textAfter: currentSelection.textAfter,
            selectionLength: currentSelection.selectedText?.length || 0,
            pageUrl: window.location.href,
            isPublic: false,
            tags: ['highlight'],
            highlightColor: selectedColor,
            category: 'general',
            priority: 'normal',
            isImportant: false,
        }

        setCurrentSelection(null)
        window.getSelection()?.removeAllRanges()

        const success = await saveAnnotation(apiData)
        // Note: applyHighlights will be triggered automatically by the useEffect when annotations change

        closeContextMenu()
    }

    // Create new annotation
    const handleCreateAnnotation = () => {
        if (!currentSelection) return
        if (!isAuthenticated) {
            setError('Please log in to create annotations.')
            if (showSnackbar) {
                showSnackbar('Please log in to create annotations.', 'warning')
            }
            closeContextMenu()
            return
        }

        const selectionData = {
            id: generateId(),
            text: currentSelection.selectedText,
            content: '',
            x: contextMenu?.mouseX || 0,
            y: contextMenu?.mouseY || 0,
            selectedText: currentSelection.selectedText,
            startXPath: currentSelection.startXPath,
            endXPath: currentSelection.endXPath,
            startOffset: currentSelection.startOffset,
            endOffset: currentSelection.endOffset,
            textBefore: currentSelection.textBefore,
            textAfter: currentSelection.textAfter,
            isNew: true,
        }

        setEditingAnnotation(selectionData)
        setAnnotationText('')
        setIsEditorOpen(true)

        setCurrentSelection(null)
        window.getSelection()?.removeAllRanges()

        closeContextMenu()
    }

    // Save annotation
    const handleSaveAnnotation = async () => {
        if (!editingAnnotation) return

        const content = annotationText.trim() || (editingAnnotation.isNew ? 'Quick Highlight' : '')

        if (!editingAnnotation.isNew && !content) {
            setError('Please enter annotation content.')
            return
        }

        if (!isAuthenticated) {
            setError('Please log in to save annotations.')
            return
        }

        const annotationData = {
            ...editingAnnotation,
            content: content,
            timestamp: new Date().toISOString(),
            color: selectedColor,
            category: selectedCategory,
            priority: selectedPriority,
            tags: annotationTags,
            isImportant: isImportant,
        }

        let success
        if (editingAnnotation.isNew) {
            const apiData = {
                documentId,
                documentType,
                selectedText: annotationData.selectedText,
                annotationContent: annotationData.content,
                startXPath: annotationData.startXPath,
                endXPath: annotationData.endXPath,
                startOffset: annotationData.startOffset,
                endOffset: annotationData.endOffset,
                textBefore: annotationData.textBefore,
                textAfter: annotationData.textAfter,
                selectionLength: annotationData.selectedText?.length || 0,
                pageUrl: window.location.href,
                isPublic: false,
                tags: annotationData.tags || [],
                highlightColor: annotationData.color || '#fff59d',
                category: annotationData.category || 'general',
                priority: annotationData.priority || 'normal',
                isImportant: annotationData.isImportant || false,
            }

            success = await saveAnnotation(apiData)
        } else {
            const updateData = {
                annotationContent: annotationData.content,
                highlightColor: annotationData.color,
                category: annotationData.category,
                priority: annotationData.priority,
                tags: annotationData.tags,
                isImportant: annotationData.isImportant,
            }
            success = await updateAnnotation(editingAnnotation.id, updateData)
        }

        if (success) {
            setIsEditorOpen(false)
            setEditingAnnotation(null)
            setAnnotationText('')
            setSelectedColor('#fff59d')
            setSelectedCategory('general')
            setSelectedPriority('normal')
            setAnnotationTags([])
            setTagsInputValue('')
            setIsImportant(false)
            // Note: applyHighlights will be triggered automatically by the useEffect when annotations change
            setCurrentSelection(null)
            window.getSelection()?.removeAllRanges()
        }
    }

    // Delete annotation
    const handleDeleteAnnotation = async (annotationId) => {
        if (!isAuthenticated) {
            setError('Please log in to delete annotations.')
            return
        }

        try {
            removeHighlightFromDOM(annotationId)

            setAnnotations((prevAnnotations) =>
                prevAnnotations.filter((ann) => ann.id !== annotationId)
            )

            setIsEditorOpen(false)
            setEditingAnnotation(null)
            setAnnotationText('')
            setSelectedColor('#fff59d')
            setSelectedCategory('general')
            setSelectedPriority('normal')
            setAnnotationTags([])
            setTagsInputValue('')
            setIsImportant(false)

            await deleteAnnotation(annotationId)
        } catch (error) {
            console.error('Error in handleDeleteAnnotation:', error)
            loadAnnotations()
        }
    }

    // Copy selected text
    const handleCopySelectedText = async () => {
        if (!currentSelection?.selectedText) {
            setError('No text selected to copy')
            closeContextMenu()
            return
        }

        try {
            if (!navigator.clipboard) {
                throw new Error('Clipboard API not available')
            }

            await navigator.clipboard.writeText(currentSelection.selectedText)
            if (showSnackbar) {
                showSnackbar('Text copied to clipboard!', 'success')
            }
        } catch (error) {
            try {
                const textArea = document.createElement('textarea')
                textArea.value = currentSelection.selectedText
                document.body.appendChild(textArea)
                textArea.select()
                const successful = document.execCommand('copy')
                document.body.removeChild(textArea)

                if (successful) {
                    if (showSnackbar) {
                        showSnackbar('Text copied to clipboard!', 'success')
                    }
                } else {
                    throw new Error('execCommand failed')
                }
            } catch (fallbackError) {
                setError('Failed to copy text to clipboard')
                if (showSnackbar) {
                    showSnackbar('Failed to copy text', 'error')
                }
            }
        }

        closeContextMenu()
    }

    // Apply highlights to content
    const applyHighlights = useCallback(() => {
        if (!containerRef.current) {
            return
        }

        const existingHighlights = containerRef.current.querySelectorAll(
            '.annotation-highlight'
        )

        existingHighlights.forEach((highlight) => {
            try {
                if (highlight._cleanup) {
                    highlight._cleanup()
                }
                const parent = highlight.parentNode
                const textContent = highlight.textContent
                const textNode = document.createTextNode(textContent)
                parent.replaceChild(textNode, highlight)
                parent.normalize()
            } catch (error) {
                console.warn('Failed to clean up highlight:', error)
            }
        })

        annotations.forEach((annotation) => {
            try {
                highlightText(annotation)
            } catch (error) {
                console.warn(`Failed to highlight annotation ${annotation.id}:`, error)
            }
        })
    }, [annotations, highlightText, containerRef])

    // Update ref after applyHighlights is defined
    applyHighlightsRef.current = applyHighlights
    
    // Track if highlights have been applied to prevent duplicate calls
    const highlightsAppliedRef = useRef(false)
    const applyHighlightsTimeoutRef = useRef(null)
    
    // Debounced highlight application to prevent flickering
    const debouncedApplyHighlights = useCallback(() => {
        if (applyHighlightsTimeoutRef.current) {
            clearTimeout(applyHighlightsTimeoutRef.current)
        }
        
        applyHighlightsTimeoutRef.current = setTimeout(() => {
            if (containerRef.current && annotations.length > 0) {
                // Check if container has actual text content (content is loaded)
                const textContent = containerRef.current.textContent?.trim()
                if (textContent && textContent.length > 0) {
                   // console.log('[usePopupAnnotations] Applying highlights for', annotations.length, 'annotations')
                    applyHighlights()
                    highlightsAppliedRef.current = true
                } else {
                   // console.log('[usePopupAnnotations] Container has no content yet, retrying...')
                    // Retry after a short delay if content not ready
                    applyHighlightsTimeoutRef.current = setTimeout(() => debouncedApplyHighlights(), 200)
                }
            }
        }, 100)
    }, [annotations, applyHighlights, containerRef])

    // Apply highlights when annotations change (single consolidated effect)
    useEffect(() => {
        if (annotations.length > 0 && containerRef.current) {
            highlightsAppliedRef.current = false
            debouncedApplyHighlights()
        }
        
        return () => {
            if (applyHighlightsTimeoutRef.current) {
                clearTimeout(applyHighlightsTimeoutRef.current)
            }
        }
    }, [annotations, debouncedApplyHighlights, containerRef])

    // Setup event listeners for text selection
    useEffect(() => {
        if (!enableAnnotations || !containerRef.current) return

        const container = containerRef.current
        const isTouch = isTouchDevice()

        const handleTouchSelection = () => {
            setTimeout(() => {
                const selection = window.getSelection()
                if (!selection || selection.rangeCount === 0) {
                    setCurrentSelection(null)
                    return
                }

                const selectedText = selection.toString().trim()
                if (!selectedText) {
                    setCurrentSelection(null)
                    return
                }

                const range = selection.getRangeAt(0)
                const startXPath = getXPath(
                    range.startContainer.nodeType === Node.TEXT_NODE
                        ? range.startContainer.parentNode
                        : range.startContainer
                )
                const endXPath = getXPath(
                    range.endContainer.nodeType === Node.TEXT_NODE
                        ? range.endContainer.parentNode
                        : range.endContainer
                )
                const { textBefore, textAfter } = getContextText(range)

                setCurrentSelection({
                    selection,
                    range,
                    selectedText,
                    startXPath,
                    endXPath,
                    startOffset: range.startOffset,
                    endOffset: range.endOffset,
                    textBefore,
                    textAfter,
                })
            }, 150)
        }

        const handleMouseUp = (e) => {
            if (e.button === 2) {
                return
            }

            setTimeout(() => {
                const selection = window.getSelection()
                if (!selection || selection.rangeCount === 0) {
                    setCurrentSelection(null)
                    setContextMenu(null)
                    return
                }

                const selectedText = selection.toString().trim()
                if (!selectedText) {
                    setCurrentSelection(null)
                    setContextMenu(null)
                    return
                }

                const range = selection.getRangeAt(0)
                const rangeContainer = range.commonAncestorContainer
                const element = rangeContainer.nodeType === Node.TEXT_NODE
                    ? rangeContainer.parentElement
                    : rangeContainer

                if (element?.closest('.annotation-highlight')) {
                    return
                }

                if (
                    e.target.closest('.MuiFab-root') ||
                    e.target.closest('.MuiDialog-root') ||
                    e.target.closest('.MuiMenu-root')
                ) {
                    return
                }

                const startXPath = getXPath(
                    range.startContainer.nodeType === Node.TEXT_NODE
                        ? range.startContainer.parentNode
                        : range.startContainer
                )
                const endXPath = getXPath(
                    range.endContainer.nodeType === Node.TEXT_NODE
                        ? range.endContainer.parentNode
                        : range.endContainer
                )
                const { textBefore, textAfter } = getContextText(range)

                setCurrentSelection({
                    selection,
                    range,
                    selectedText,
                    startXPath,
                    endXPath,
                    startOffset: range.startOffset,
                    endOffset: range.endOffset,
                    textBefore,
                    textAfter,
                })

                setContextMenu({
                    mouseX: e.clientX,
                    mouseY: e.clientY,
                    selection,
                    range,
                    selectedText,
                })
            }, 50)
        }

        const handleContextMenuPrevent = (e) => {
            if (contextMenu || container.contains(e.target)) {
                e.preventDefault()
                e.stopPropagation()
                return false
            }
        }

        const handleClearSelection = (e) => {
            if (e.button === 2) {
                return
            }

            if (
                e.target.closest('.MuiFab-root') ||
                e.target.closest('.annotation-highlight') ||
                e.target.closest('.MuiDialog-root') ||
                e.target.closest('.MuiMenu-root')
            ) {
                return
            }

            const selection = window.getSelection()
            if (selection && !selection.toString().trim()) {
                setCurrentSelection(null)
                setContextMenu(null)
            }
        }

        if (isTouch) {
            document.addEventListener('selectionchange', handleTouchSelection)
            container.addEventListener('touchend', handleTouchSelection)
            document.addEventListener('touchstart', handleClearSelection)
        } else {
            container.addEventListener('mouseup', handleMouseUp)
            container.addEventListener('contextmenu', handleContextMenuPrevent)
            document.addEventListener('contextmenu', handleContextMenuPrevent)
            document.addEventListener('mousedown', handleClearSelection)
        }

        return () => {
            if (isTouch) {
                document.removeEventListener('selectionchange', handleTouchSelection)
                container.removeEventListener('touchend', handleTouchSelection)
                document.removeEventListener('touchstart', handleClearSelection)
            } else {
                container.removeEventListener('mouseup', handleMouseUp)
                container.removeEventListener('contextmenu', handleContextMenuPrevent)
                document.removeEventListener('contextmenu', handleContextMenuPrevent)
                document.removeEventListener('mousedown', handleClearSelection)
            }
        }
    }, [enableAnnotations, isTouchDevice, getXPath, getContextText, contextMenu, containerRef])

    // Escape key handler
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setContextMenu(null)
                setCurrentSelection(null)
                window.getSelection()?.removeAllRanges()
            }
        }

        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setIsEditorOpen(false)
            setEditingAnnotation(null)
            setContextMenu(null)
            setTooltip({ show: false, content: '', x: 0, y: 0 })
        }
    }, [])

    // Tags input handlers
    const handleTagsInputChange = (inputValue) => {
        setTagsInputValue(inputValue)

        if (inputValue.includes(',') || inputValue.includes('  ')) {
            const newTags = inputValue
                .split(/[,\s]+/)
                .map((tag) => tag.trim())
                .filter((tag) => tag.length > 0)
                .filter((tag) => !annotationTags.includes(tag))

            if (newTags.length > 0) {
                setAnnotationTags([...annotationTags, ...newTags])
                const lastDelimiterIndex = Math.max(
                    inputValue.lastIndexOf(','),
                    inputValue.lastIndexOf('  ')
                )
                const remainingInput =
                    lastDelimiterIndex >= 0
                        ? inputValue.substring(lastDelimiterIndex + 1).trim()
                        : ''
                setTagsInputValue(remainingInput)
            }
        }
    }

    const handleTagsKeyDown = (e) => {
        const inputValue = e.target.value

        if (e.key === 'Enter' || e.key === 'Tab' || e.key === ' ' || e.key === ',') {
            e.preventDefault()

            const trimmedValue = inputValue.trim()
            if (trimmedValue && !annotationTags.includes(trimmedValue)) {
                setAnnotationTags([...annotationTags, trimmedValue])
                setTagsInputValue('')
            }
        }
    }

    // Highlight colors array
    const highlightColors = [
        { color: '#fff59d', label: 'Yellow' },
        { color: '#a5d6a7', label: 'Green' },
        { color: '#90caf9', label: 'Blue' },
        { color: '#f48fb1', label: 'Pink' },
        { color: '#ce93d8', label: 'Purple' },
        { color: '#ffcc80', label: 'Orange' },
    ]

    // Categories
    const categories = [
        { value: 'general', label: 'General' },
        { value: 'interpretation', label: 'Interpretation' },
        { value: 'example', label: 'Example' },
        { value: 'question', label: 'Question' },
    ]

    // Priorities
    const priorities = [
        { value: 'low', label: 'Low' },
        { value: 'normal', label: 'Normal' },
        { value: 'high', label: 'High' },
    ]

    // Context Menu Component
    const ContextMenuComponent = (
        <Menu
            open={Boolean(contextMenu)}
            onClose={closeContextMenu}
            anchorReference='anchorPosition'
            anchorPosition={
                contextMenu
                    ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                    : undefined
            }
            sx={{ zIndex: 10001 }}
            PaperProps={{
                sx: {
                    minWidth: 200,
                    boxShadow: theme.shadows[8],
                },
            }}
        >
            {isAuthenticated ? (
                <>
                    <MenuItem onClick={handleCopySelectedText} sx={{ gap: 1 }}>
                        <ContentCopyRoundedIcon fontSize='small' />
                        Copy Selected Text
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleQuickHighlight} sx={{ gap: 1 }}>
                        <Highlight fontSize='small' />
                        Quick Highlight
                    </MenuItem>
                    <MenuItem onClick={handleCreateAnnotation} sx={{ gap: 1 }}>
                        <AddIcon fontSize='small' />
                        Add Annotation
                    </MenuItem>
                </>
            ) : (
                <>
                    <MenuItem onClick={handleCopySelectedText} sx={{ gap: 1 }}>
                        <ContentCopyRoundedIcon fontSize='small' />
                        Copy Selected Text
                    </MenuItem>
                    <Divider />
                    <MenuItem disabled sx={{ gap: 1, opacity: 0.6 }}>
                        <Highlight fontSize='small' />
                        Log in to highlight
                    </MenuItem>
                </>
            )}
        </Menu>
    )

    // Touch FAB Component
    const TouchFabComponent = isTouchDevice() && currentSelection && isAuthenticated ? (
        <Zoom in={Boolean(currentSelection)}>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 100,
                    right: 20,
                    zIndex: 10001,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <Tooltip title='Quick Highlight' placement='top' arrow enterDelay={300}>
                    <Fab
                        size='small'
                        onClick={handleQuickHighlight}
                        aria-label='Quick Highlight'
                        sx={{
                            backgroundColor: themeColors.primary,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: themeColors.hover,
                            },
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        }}
                    >
                        <Highlight fontSize='small' />
                    </Fab>
                </Tooltip>
                <Tooltip title='Add Annotation' placement='top' arrow enterDelay={300}>
                    <Fab
                        size='small'
                        onClick={handleCreateAnnotation}
                        aria-label='Add Annotation'
                        sx={{
                            backgroundColor: themeColors.primary,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: themeColors.hover,
                            },
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        }}
                    >
                        <AddIcon fontSize='small' />
                    </Fab>
                </Tooltip>
            </Box>
        </Zoom>
    ) : null

    // Tooltip Component
    const TooltipComponent = tooltip.show ? (
        <Box
            sx={{
                position: 'fixed',
                left: tooltip.x,
                top: tooltip.y,
                transform: 'translate(-50%, -100%)',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                maxWidth: 300,
                zIndex: 10002,
                pointerEvents: 'none',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
            }}
        >
            {tooltip.content}
        </Box>
    ) : null

    // Editor Dialog Component
    const EditorDialogComponent = (
        <Dialog
            open={isEditorOpen}
            onClose={() => setIsEditorOpen(false)}
            maxWidth='sm'
            fullWidth
            fullScreen={isMobile}
            sx={{ zIndex: 10001 }}
            PaperProps={{
                sx: {
                    margin: { xs: 2, sm: 4 },
                    maxHeight: { xs: 'calc(100vh - 110px)', sm: '90vh' },
                    borderRadius: { xs: 2, sm: 2 },
                    border: { xs: `2px solid ${themeColors.secondary}`, sm: 'none' },
                },
            }}
            TransitionProps={{
                timeout: {
                    enter: 400,
                    exit: 200,
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingX: { xs: 2, sm: 3 },
                }}
            >
                <Typography variant='h6'>
                    {editingAnnotation?.isNew ? 'Add Annotation' : 'Edit Annotation'}
                </Typography>
                <IconButton onClick={() => setIsEditorOpen(false)} size='small'>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    paddingY: { xs: 2, sm: 3 },
                    paddingX: 2,
                }}
            >
                {editingAnnotation && (
                    <Box sx={{ mb: 2 }}>
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ mb: 1 }}
                        >
                            Selected text:
                        </Typography>
                        <Paper
                            sx={{
                                p: 1,
                                backgroundColor: theme.palette.grey[100],
                                fontStyle: 'italic',
                                minHeight: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                border: `1px solid ${theme.palette.grey[300]}`,
                                borderRadius: 1,
                            }}
                        >
                            <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
                                {editingAnnotation.selectedText ||
                                    editingAnnotation.text ||
                                    currentSelection?.selectedText ||
                                    'No text selected'}
                            </Typography>
                        </Paper>
                    </Box>
                )}

                <Stack direction='row' spacing={2} sx={{ mb: 2 }}>
                    <FormControl size='small' sx={{ minWidth: 120 }}>
                        <InputLabel
                            sx={{
                                '&.Mui-focused': {
                                    color: themeColors.primary,
                                },
                            }}
                        >
                            Category
                        </InputLabel>
                        <Select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            label='Category'
                            displayEmpty
                            sx={{
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: themeColors.primary,
                                },
                            }}
                            MenuProps={{
                                disablePortal: false,
                                sx: {
                                    zIndex: 99999,
                                },
                                PaperProps: {
                                    sx: {
                                        zIndex: 99999,
                                        '& .MuiMenuItem-root': {
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#e8e8e8',
                                                '&:hover': {
                                                    backgroundColor: '#d5d5d5',
                                                },
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem value='general'>
                                General
                            </MenuItem>
                            <MenuItem value='interpretation'>Interpretation</MenuItem>
                            <MenuItem value='example'>Example</MenuItem>
                            <MenuItem value='question'>Question</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                <Stack
                    direction='row'
                    spacing={2}
                    sx={{ mb: 2, alignItems: 'flex-start' }}
                >
                    <TextField
                        size='small'
                        label='Tags (comma separated)'
                        value={tagsInputValue}
                        onChange={(e) => handleTagsInputChange(e.target.value)}
                        onKeyDown={handleTagsKeyDown}
                        sx={{
                            flexGrow: 1,
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: themeColors.primary,
                                },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: themeColors.primary,
                            },
                        }}
                        placeholder='e.g., regulation, compliance, review'
                        helperText='Press Enter, Tab, Space, or Comma to add tags'
                    />

                    <Tooltip title='Mark as important'>
                        <IconButton
                            onClick={() => setIsImportant(!isImportant)}
                            color={isImportant ? 'warning' : 'default'}
                            sx={{
                                mt: 0.5,
                                color: isImportant ? '#ffd700' : 'inherit',
                            }}
                        >
                            {isImportant ? <Star /> : <StarBorder />}
                        </IconButton>
                    </Tooltip>
                </Stack>

                {annotationTags.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Stack direction='row' spacing={1} flexWrap='wrap'>
                            {annotationTags.map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={tag}
                                    size='small'
                                    onDelete={() => {
                                        const newTags = annotationTags.filter(
                                            (_, i) => i !== index
                                        )
                                        setAnnotationTags(newTags)
                                        setTagsInputValue(newTags.join(', '))
                                    }}
                                    color='primary'
                                    variant='outlined'
                                />
                            ))}
                        </Stack>
                    </Box>
                )}

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ mt: 2 }}>
                    <Typography
                        variant='caption'
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            mb: 0.5,
                            display: 'block',
                        }}
                    >
                        Note:
                    </Typography>
                    <TextField
                        multiline
                        rows={4}
                        value={annotationText}
                        onChange={(e) => setAnnotationText(e.target.value)}
                        placeholder='Enter your annotation here...'
                        variant='outlined'
                        fullWidth
                        InputProps={{
                            sx: {
                                direction: 'ltr',
                                textAlign: 'left',
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: themeColors.primary,
                                },
                            },
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    gap: 1,
                    padding: 2,
                    paddingTop: 1,
                    flexWrap: 'wrap',
                }}
            >
                {editingAnnotation && !editingAnnotation.isNew && (
                    <Button
                        onClick={() => handleDeleteAnnotation(editingAnnotation.id)}
                        color='error'
                        startIcon={<Delete />}
                        sx={{
                            minWidth: { xs: 'auto', sm: '90px' },
                        }}
                    >
                        Delete
                    </Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    onClick={() => setIsEditorOpen(false)}
                    sx={{
                        color: themeColors.primary,
                        '&:hover': {
                            backgroundColor: 'rgba(35, 37, 54, 0.08)',
                        },
                        minWidth: { xs: '90px', sm: '100px' },
                        paddingX: 2,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSaveAnnotation}
                    variant='contained'
                    disabled={
                        editingAnnotation?.isNew ? false : !annotationText.trim()
                    }
                    sx={{
                        backgroundColor: themeColors.primary,
                        '&:hover': {
                            backgroundColor: themeColors.hover,
                        },
                        '&:disabled': {
                            backgroundColor: '#cccccc',
                            color: '#666666',
                        },
                        minWidth: { xs: '90px', sm: '100px' },
                        paddingX: 2,
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )

    // CSS Styles
    const AnnotationStyles = (
        <style>
            {`
        .annotation-highlight {
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          display: inline !important;
          line-height: inherit !important;
          font-size: inherit !important;
          font-family: inherit !important;
          font-weight: inherit !important;
          letter-spacing: inherit !important;
          word-spacing: inherit !important;
        }
        
        .annotation-highlight {
          cursor: pointer !important;
        }
        
        .annotation-highlight a {
          cursor: pointer !important;
          pointer-events: auto !important;
        }
        
        .annotation-highlight a:hover {
          text-decoration: underline !important;
        }
        
        .annotation-highlight:hover {
          background-color: #ffeaa7 !important;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
      `}
        </style>
    )

    return {
        // State
        annotations,
        loading,
        error,
        successMessage,
        isEditorOpen,
        editingAnnotation,
        currentSelection,
        contextMenu,
        tooltip,

        // Actions
        loadAnnotations,
        applyHighlights,
        closeContextMenu,
        handleQuickHighlight,
        handleCreateAnnotation,
        handleSaveAnnotation,
        handleDeleteAnnotation,
        handleEditAnnotation,
        handleCopySelectedText,

        // UI Components
        ContextMenuComponent,
        TouchFabComponent,
        TooltipComponent,
        EditorDialogComponent,
        AnnotationStyles,

        // For external use
        isAuthenticated,
        enableAnnotations,
    }
}

export default usePopupAnnotations
